import React from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import {
    ModalContent,
    ModalHeader,
    ModalBody,
    Button,
    HStack,
    SimpleGrid,
    Alert,
    AlertIcon,
    GridItem,
    useToast,
} from '@chakra-ui/react';
import { forms, fields } from '~/pages/projects/meta/data/correspondence.schema';
import { Form, renderField } from '~/components/Form/Form';
import { useModal, useDebounce, useYupValidationResolver, useViewer, usePermission } from '~/hooks';
import { Dokument, Korrespondenz, KorrespondenzDokument, Snippet } from '~/gql/ucpw/graphql';
import { useSubProjectMetadata } from '~/pages/projects/hooks/useSubProjectMetadata';
import { isPxConnectSource, type PxBearbeitungsstand } from '~/meta/data/px-connect';
import { Option } from '~/components/Form/SnippetSelect';
import { useAsset } from '~/hooks/useAsset';
import { join, resolveFormFields, withMaxSerializedLength } from '~/utils';
import { HasPermission } from '~/layout/HasPermission';
// PX Connect: Typ/Subtyp-Erkennung + Terminerfassungs-Trigger
import {
    isPxConnectType,
    isPxConnectTerminmitteilung,
    PXC_SUBTYP_NUR_SYSTEM_KUERZEL,
    PXC_SUBTYP_TERMINERFASSUNG_KUERZEL,
} from '~/pages/projects/meta/data/pxConnect';
import { PxConnectTerminBlock } from '~/pages/projects/ui/PxConnectTerminBlock';
import { usePxConnectTermine } from '~/pages/projects/hooks/usePxConnectTermine';
import { usePxConnectSchadenberichtSent } from '~/pages/projects/hooks/usePxConnectSchadenberichtSent';
import { useProjectIds } from '~/pages/projects/hooks/useProjectIds';
import { isEqual } from 'lodash';
import { format } from 'date-fns';
import log from '~/log';

const NO_INVOICE_DOCUMENTS = 'NO_INVOICE_DOCUMENTS';
const NO_OFFER_DOCUMENTS = 'NO_OFFER_DOCUMENTS';
const GDV_SUPPORTED_FILE_TYPES = ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'];

export const CorrespondenceModal = (props?: any) => {
    const { hasServiceRequestId, projectMetadata, subprojects } = useSubProjectMetadata();
    const { analyze } = useAsset();
    const [xmlErrors, setXmlErrors] = React.useState<(string | number)[]>([]);
    const [showXmlAlert, setShowXmlAlert] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState('');
    const { state, dispatch, onClose, isOpen } = useModal();
    const {
        createCorrespondenceAndDocumentsMutation,
        updateCorrespondenceAndDocumentsMutation,
        createTaskMutation,
        createTaskFunction,
        deleteTaskFunction,
        // Schnittstellen-Nachricht (GDV / PX Connect): nur ansehen, nicht bearbeiten —
        // gesetzt von `isLockedCorrespondence` in useCorrespondence.
        isLocked = false,
    } = state?.modals?.props || props || {};
    const subprojectId =
        state?.modals?.props?.subprojectId ||
        props?.subprojectId ||
        state?.row?.subprojekt?.id ||
        state?.rows?.row?.subprojekt?.id;

    const viewer = useViewer();

    const editorEmployeeId = viewer.app?.mitarbeiterId;

    const { row: correspondence = {}, rowId } = state?.rows || {};

    const formFields = resolveFormFields(forms.correspondence, fields.correspondence);
    const id = useDebounce(rowId, 200);

    const defaultValues = {
        ...formFields.defaultValues,
        ...formFields.toForm({
            ...correspondence,
            ...(!rowId && editorEmployeeId && { mitarbeiterIdSachbearbeiter: editorEmployeeId }),
        }),
        time: format(
            correspondence?.zeit ? new Date(correspondence?.zeit) : new Date(),
            'dd.MM.yyyy HH:mm'
        ),
    };

    const {
        handleSubmit,
        control,
        register,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues,
        shouldFocusError: true,
        resolver: useYupValidationResolver(
            yup.object({
                typeSnippetId: yup.object().nullable().required('Typ ist erforderlich'),
                subtypSnippetId: yup
                    .object()
                    .nullable()
                    .when('typeSnippetId', {
                        is: (typeSnippetId: any) =>
                            typeSnippetId?.key === 'GDV' || isPxConnectType(typeSnippetId),
                        then: (schema) => schema.required('Subtyp ist erforderlich'),
                        otherwise: (schema) => schema.nullable(),
                    }),
                document: yup.array().when('typeSnippetId', {
                    is: (typeSnippetId: any) => typeSnippetId?.key === 'GDV',
                    then: (schema) =>
                        schema.test(
                            'gdv-file-types',
                            `Für GDV-Nachrichten sind nur folgende Dateitypen erlaubt: ${GDV_SUPPORTED_FILE_TYPES.join(
                                ', '
                            )}`,
                            (documents) => {
                                if (!documents || documents.length === 0) {
                                    return true; // Allow empty documents, other validation will handle required
                                }
                                return documents.every((doc: any) => {
                                    const mimeType = doc?.dokument?.mimeType;
                                    if (!mimeType) {
                                        return true; // Allow documents without mimeType (existing uploads)
                                    }
                                    return GDV_SUPPORTED_FILE_TYPES.includes(mimeType);
                                });
                            }
                        ),
                    otherwise: (schema) => schema.nullable(),
                }),
                // korrespondenz.freitext VARCHAR(10000) — Limit gilt für das serialisierte JSON (ADR-0004)
                comment: withMaxSerializedLength(yup.array(), 10000).test('comment', 'Bemerkung ist erforderlich', (value: any) => {
                    console.log('yup', value);
                    return !isEqual(value, [
                        {
                            type: 'paragraph',
                            children: [
                                {
                                    text: '',
                                },
                            ],
                        },
                    ]);
                }),
            })
        ),
    });
    const watchFields = watch();

    // GDV- vs. PX-Connect-Typ/-Subtyp werden anhand des Quellsystems (isPxConnectSource)
    // ein-/ausgeblendet. Mit Subprojekt-Bezug zählt dessen Metadata, sonst (Projektüberblick)
    // die des Projekts.
    const isPxConnectContext = subprojectId
        ? isPxConnectSource(subprojects.find((subproject) => subproject.id === subprojectId)?.metadata)
        : isPxConnectSource(projectMetadata);

    // PX-Connect-Zustand: Typ = echtes 'PXC'-Snippet, Subtypen = echte „PXSubtyp"-Snippets.
    // Eine PXC-Nachricht wird normal als Korrespondenz gespeichert; beim Subtyp „Terminerfassung"
    // werden zusätzlich die Termine nach Projekt.metadata geschrieben (ADR 0005).
    const isPxcType = isPxConnectType(watchFields.typeSnippetId);
    const showPxcTermin = isPxConnectTerminmitteilung(
        watchFields.typeSnippetId,
        watchFields.subtypSnippetId
    );
    // Terminerfassung persistiert in `Projekt.metadata` (pxConnectTermine, ADR 0005) —
    // derselbe Datensatz, den auch das eigenständige PxConnectTerminModal liest/schreibt.
    // Die Termine selbst landen also NICHT in der Korrespondenz; die Korrespondenz-Zeile
    // entsteht auf diesem Weg trotzdem zusätzlich (siehe onSubmit) — anders als im
    // eigenständigen Modal, das nur die Metadaten schreibt.
    const { projectId } = useProjectIds();
    const pxcTermine = usePxConnectTermine({
        projectId,
        active: Boolean(isOpen) && showPxcTermin,
    });
    // Schadenbericht (ADR 0006): stammt diese Korrespondenz aus dem „Korrespondenz
    // vorbereiten"-Flow, trägt das Row-Objekt die Projekt-Id als Marker; nach dem
    // Speichern wird darüber `sentAt` im Draft gesetzt.
    const { markSent } = usePxConnectSchadenberichtSent();
    const pxSchadenberichtProjektId = (correspondence as any)?.__pxSchadenberichtProjektId as
        | number
        | undefined;
    // Stand des Berichts beim Vorbereiten (UCPWO-410); wird je Korrespondenz festgehalten.
    const pxSchadenberichtStand = (correspondence as any)?.__pxSchadenberichtStand as
        | PxBearbeitungsstand
        | undefined;

    log.debug('CorrespondenceModal', {
        id,
        rowId,
        correspondence,
        subprojectId,
        state,
        defaultValues,
        formFields,
        watchFields,
        errors,
    });

    /** Permit */
    const hasEditAllCorrespondence = viewer.hasPermission(
        'additional.editAllCorrespondence',
        'enabled'
    );
    const { canCreate, canEdit, canView } = usePermission('project.correspondence');
    // Der Terminerfassungs-Block hängt am selben Recht wie der Knopf im Projektüberblick — sonst
    // schützt das Recht nur einen der beiden Einstiege zum selben Datensatz (ADR 0005).
    const { canCreate: canCreatePxcTermin } = usePermission('pxconnect.terminerfassung');
    const toast = useToast();
    const readOnly = rowId && canView && !(canCreate || canEdit || hasEditAllCorrespondence);
    const canEditCorrespondence = React.useMemo(() => {
        if (!rowId) return (canCreate || hasEditAllCorrespondence) && !isLocked;
        if (hasEditAllCorrespondence && !isLocked) return true;
        const isOwner = correspondence?.mitarbeiterIdUrheber === editorEmployeeId;
        const hasNoOwner = correspondence?.mitarbeiterIdUrheber === null;
        return canEdit && !isLocked && (isOwner || hasNoOwner);
    }, [
        rowId,
        canCreate,
        canEdit,
        hasEditAllCorrespondence,
        isLocked,
        correspondence?.mitarbeiterIdUrheber,
        editorEmployeeId,
    ]);

    const modalConfig = React.useMemo(() => {
        const isViewMode = readOnly || !canEditCorrespondence || isLocked;
        const isEditMode = rowId && canEditCorrespondence;

        return {
            title: isViewMode
                ? 'Korrespondenz ansehen'
                : isEditMode
                ? 'Korrespondenz bearbeiten'
                : 'Neue Korrespondenz',
            cancelButtonLabel: isViewMode ? 'Schließen' : 'Abbrechen',
            cursor: isViewMode ? 'not-allowed' : 'auto',
        };
    }, [readOnly, canEditCorrespondence, isLocked, rowId]);

    function parseCustomDate(dateStr: string) {
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('.').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);

        return new Date(year, month - 1, day, hour, minute);
    }

    const validateDocumentsXML = React.useCallback(
        async (values: any) => {
            const { typeSnippetId, subtypSnippetId, document } = values;
            const errors: (string | number)[] = [];

            // GDV „Rechnung" und PX Connect „Rechnung" (RN) erzeugen beide ein
            // invoice.document-Event aus der angehängten E-Rechnung — ohne gültiges
            // XML skipt der Worker still, daher hier vorvalidieren (UCPWO-377 Schnitt 3).
            const isInvoiceMessage =
                (typeSnippetId?.key === 'GDV' && subtypSnippetId?.label === 'Rechnung') ||
                (isPxConnectType(typeSnippetId) && subtypSnippetId?.key === 'RN');

            // PX Connect „Angebot" (AN): das offer.document-Event braucht ein aus
            // einem Angebot erzeugtes Dokument (angebotId) — sonst skipt der Worker still.
            if (isPxConnectType(typeSnippetId) && subtypSnippetId?.key === 'AN') {
                const offerDocuments =
                    document?.filter(
                        (doc: KorrespondenzDokument) => (doc?.dokument as Dokument)?.angebotId
                    ) || [];
                if (offerDocuments.length === 0) {
                    return [NO_OFFER_DOCUMENTS];
                }
            }

            if (isInvoiceMessage) {
                // Check if there's at least one document with typSnippet label 'Rechnung'
                const rechnungDocuments =
                    document?.filter(
                        (
                            doc: KorrespondenzDokument & {
                                dokument: Dokument & { typSnippet: Snippet & { label: string } };
                            }
                        ) => {
                            const { dokument } = doc || {};
                            return dokument?.typSnippet?.label === 'Rechnung';
                        }
                    ) || [];

                if (rechnungDocuments.length === 0) {
                    // Return a special error code to indicate no Rechnung documents found
                    return [NO_INVOICE_DOCUMENTS];
                }

                // Validate XML for each Rechnung document
                for (const [idx, doc] of document.entries()) {
                    const { dokument } = doc || {};
                    if (dokument?.typSnippet?.label === 'Rechnung' && dokument?.pfad) {
                        const { status } = await analyze(dokument.pfad);
                        const hasXML = status === 'valid';
                        if (!hasXML) {
                            errors.push(idx);
                        }
                    }
                }
            }

            return errors;
        },
        [analyze]
    );

    /**
     * Speichert die Korrespondenz und meldet über den Rückgabewert, **ob** gespeichert wurde.
     *
     * Der Rückgabewert ist nötig, weil die Aufrufer ihre Folgeaktion (schließen / Aufgabe
     * anlegen) sonst am State `xmlErrors` entscheiden müssten — den dieselbe Closure setzt
     * und deshalb noch mit dem Wert des vorherigen Klicks liest. Das Verhalten war dadurch
     * um einen Klick versetzt: im ungünstigen Fall wurde das Modal zurückgesetzt, obwohl die
     * Validierung fehlgeschlagen und nichts gespeichert war.
     */
    const onSubmit = React.useCallback(
        async (values: any): Promise<boolean> => {
            log.debug('onSubmit.values', JSON.stringify(values, null, 2));

            // Ohne PX-Terminrecht bleibt der Datensatz unangetastet: unverändert → Speichern
            // überspringen, die Korrespondenz entsteht trotzdem (sonst ließe sich ein alter
            // Terminerfassungs-Eintrag nicht mehr bearbeiten, und jedes Speichern würde `savedAt`
            // neu stempeln und ein Ereignis auslösen). Verändert → abbrechen mit Meldung statt
            // still verwerfen; über die gesperrten Felder ist das nicht erreichbar, es ist die
            // Sicherung dahinter.
            if (showPxcTermin && !canCreatePxcTermin && pxcTermine.hasChanges) {
                toast({
                    title: 'Terminerfassung nicht gespeichert',
                    description: 'Für Änderungen an der PX Terminerfassung fehlt die Berechtigung.',
                    status: 'error',
                    position: 'top',
                    duration: 5000,
                    isClosable: true,
                });
                return false;
            }

            // PX Connect + Terminerfassung: zuerst die Termine nach Projekt.metadata schreiben
            // (ADR 0005), danach wird die Korrespondenz ganz normal angelegt. Schlägt der
            // Metadata-Save fehl, brechen wir ab (dann entsteht auch keine Korrespondenz).
            if (showPxcTermin && canCreatePxcTermin) {
                const savedTermine = await pxcTermine.save();
                if (!savedTermine) {
                    log.warn('onSubmit.pxcTermine', 'Terminerfassung konnte nicht gespeichert werden');
                    return false;
                }
            }

            // Validate XML before proceeding
            const xmlValidationErrors = await validateDocumentsXML(values);
            if (xmlValidationErrors.length > 0) {
                setXmlErrors(xmlValidationErrors);
                setShowXmlAlert(true);
                log.warn(
                    'onSubmit.validation',
                    'Cannot save: XML validation failed',
                    xmlValidationErrors
                );
                return false;
            }

            const addValues = {
                subprojectId: subprojectId || (correspondence as Korrespondenz).subprojektId,
            };
            const {
                korrespondenzDokument = [],
                zeit,
                ...data
            } = formFields.toGql(values, addValues) as any;
            log.debug('onSubmit.data', JSON.stringify(data, null, 2));

            console.log('onSubmit.compare', { data, zeit, correspondence });

            const documents = korrespondenzDokument.map((document: any) => {
                const { dokument } = document || {};
                const typSnippet = dokument?.typSnippet;
                return {
                    ...(typSnippet && {
                        typSnippetId: dokument?.typSnippet?.id || dokument?.typSnippet?.value,
                    }),
                    ...(dokument?.angebotId && { angebotId: dokument?.angebotId }),
                    subprojektId: subprojectId,
                    name: dokument?.name || '',
                    pfad: dokument?.pfad || '',
                    beschreibung: dokument?.beschreibung || '',
                    uploadDatum: dokument?.uploadDatum || new Date(),
                };
            });

            const { documentsToUpdate, documentsToCreate } = korrespondenzDokument.reduce(
                (acc: any, document: any) => {
                    const { dokumentId: id, dokument } = document || {};
                    const typSnippet = dokument?.typSnippet;
                    const data = {
                        ...(typSnippet && {
                            typSnippetId: dokument?.typSnippet?.id || dokument?.typSnippet?.value,
                        }),
                        subprojektId: subprojectId,
                        name: dokument?.name || '',
                        pfad: dokument?.pfad || '',
                        beschreibung: dokument?.beschreibung || '',
                        uploadDatum: dokument?.uploadDatum || new Date(),
                    };

                    if (id) {
                        acc.documentsToUpdate.push({ id, data });
                    } else {
                        acc.documentsToCreate.push(data);
                    }

                    return acc;
                },
                { documentsToUpdate: [], documentsToCreate: [] }
            );

            const response = id
                ? await updateCorrespondenceAndDocumentsMutation({
                      variables: {
                          id,
                          version: correspondence?.version,
                          forceOverwrite: true,
                          correspondence: {
                              ...data,
                              ...(values?.typeSnippetId?.key !== 'GDV' &&
                                  !isPxConnectType(values?.typeSnippetId) && {
                                      subtypSnippetId: 0,
                                  }),
                              subprojektId: subprojectId,
                          },
                          documentsToUpdate,
                          documentsToCreate,
                      },
                  })
                : await createCorrespondenceAndDocumentsMutation({
                      variables: {
                          correspondence: {
                              ...data,
                              mitarbeiterIdUrheber: editorEmployeeId,
                              subprojektId: subprojectId,
                              zeit: parseCustomDate(zeit),
                          },
                          documents,
                      },
                  });
            log.debug('onSubmit.response', JSON.stringify(response, null, 2));

            // Schadenbericht-Marker vorhanden → „versendet am" stempeln (ADR 0006).
            // Läuft bewusst nach dem erfolgreichen Korrespondenz-Save; Fehler werden
            // im Hook geschluckt und beeinflussen das Speicherergebnis nicht.
            if (pxSchadenberichtProjektId) {
                const korrespondenzId =
                    id || (response as any)?.data?.app?.item?.item?.id || undefined;
                await markSent(pxSchadenberichtProjektId, {
                    korrespondenzId,
                    stand: pxSchadenberichtStand,
                });
            }

            return true;
        },
        [
            id,
            editorEmployeeId,
            subprojectId,
            correspondence,
            validateDocumentsXML,
            showPxcTermin,
            canCreatePxcTermin,
            pxcTermine,
            toast,
            pxSchadenberichtProjektId,
            pxSchadenberichtStand,
            markSent,
        ]
    );

    const onSubmitWithOnClose = async (values: any) => {
        setSubmitting('submit');
        // try/finally, damit ein Fehler im Submit den Button nicht dauerhaft im Ladezustand
        // stehen lässt (so sah das Aufhängen beim Anhängen einer E-Rechnung aus).
        try {
            if (await onSubmit(values)) {
                onReset();
            }
        } finally {
            setSubmitting('');
        }
    };

    const onSubmitWithRedirect = async (values: any) => {
        setSubmitting('submit-and-task');
        try {
            if (!(await onSubmit(values))) return;

            dispatch?.({ type: 'resetState' });
            dispatch?.({
                type: 'setModal',
                data: {
                    modal: 'TaskModal',
                    props: {
                        createMutation: createTaskMutation,
                        createTaskFunction,
                        deleteTaskFunction,
                    },
                },
            });
            dispatch?.({
                type: 'setRow',
                data: { row: { original: { subprojekt: correspondence?.subprojekt || {} } } },
            });
            dispatch?.({ type: 'formState', data: values });
        } finally {
            setSubmitting('');
        }
    };

    const onReset = () => {
        dispatch?.({ type: 'resetState' });
        onClose?.();
        setSubmitting('');
        setShowXmlAlert(false);
    };

    React.useEffect(() => {
        const subscription = watch(async (values, { type }) => {
            console.log('Changed:', values, type);

            const errors = await validateDocumentsXML(values);
            setXmlErrors(errors);

            if (showXmlAlert) {
                setShowXmlAlert(false);
            }

            // Subtyp auch für PXC-Typ behalten (sonst Reset des Termin-Triggers)
            if (
                values?.typeSnippetId?.key !== 'GDV' &&
                !isPxConnectType(values?.typeSnippetId) &&
                values?.subtypSnippetId
            ) {
                setValue('subtypSnippetId', undefined);
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, validateDocumentsXML, showXmlAlert]);

    return (
        <ModalContent maxWidth="container.md" rounded="none">
            <ModalHeader
                justifyContent="space-between"
                alignItems="center"
                display="flex"
                borderBottomWidth={1}
                borderColor="gray.200"
                mb={6}
                p={5}
            >
                {modalConfig.title}
                <HStack>
                    <Button
                        data-test-id="button-cancel"
                        isDisabled={!!submitting}
                        variant="outline"
                        onClick={onReset}
                    >
                        {modalConfig.cancelButtonLabel}
                    </Button>
                    {canEditCorrespondence && (
                        <>
                            <HasPermission resource="project.tasks" permission="create">
                                <Button
                                    isLoading={submitting === 'submit-and-task'}
                                    isDisabled={
                                        submitting === 'submit' ||
                                        showXmlAlert ||
                                        isUploading ||
                                        // wie „Speichern": bei Terminerfassung müssen Termine gültig sein
                                        (showPxcTermin &&
                                            (pxcTermine.hasErrors || !pxcTermine.ready))
                                    }
                                    data-test-id="button-save-correspondence-task-next"
                                    variant="outline"
                                    onClick={handleSubmit(onSubmitWithRedirect)}
                                >
                                    Speichern + Aufgabe
                                </Button>
                            </HasPermission>
                            <Button
                                isLoading={submitting === 'submit'}
                                isDisabled={
                                    submitting === 'submit-and-task' ||
                                    showXmlAlert ||
                                    isUploading ||
                                    // PXC + Terminerfassung: Termine müssen gültig sein (sie werden
                                    // zusätzlich nach Projekt.metadata gespeichert, ADR 0005).
                                    (showPxcTermin &&
                                        (pxcTermine.hasErrors || !pxcTermine.ready))
                                }
                                data-test-id="button-save-correspondence"
                                colorScheme="blue"
                                onClick={handleSubmit(onSubmitWithOnClose)}
                            >
                                Speichern
                            </Button>
                        </>
                    )}
                </HStack>
            </ModalHeader>
            <ModalBody cursor={modalConfig.cursor}>
                <Form onSubmit={handleSubmit(onSubmitWithOnClose)}>
                    <SimpleGrid spacing={4} columns={6} mb={6}>
                        {formFields.fields.map((field: any) => (
                            <React.Fragment key={field.name}>
                                {/* Terminerfassungs-Block zwischen Anhänge und Bemerkung */}
                                {field.name === 'comment' && showPxcTermin && (
                                    <GridItem colSpan={6}>
                                        <PxConnectTerminBlock
                                            // ohne PX-Terminrecht nur ansehen — onSubmit lässt den Datensatz dann aus
                                            isDisabled={
                                                readOnly || pxcTermine.loading || !canCreatePxcTermin
                                            }
                                            values={pxcTermine.values}
                                            ortstermine={pxcTermine.ortstermine}
                                            persisted={pxcTermine.persisted}
                                            onValuesChange={pxcTermine.setValues}
                                            onOrtstermineChange={pxcTermine.setOrtstermine}
                                        />
                                    </GridItem>
                                )}
                                {renderField({
                                    field,
                                    control,
                                    register,
                                    errors,
                                    watchFields,
                                    context: {
                                        errors,
                                        subprojectId,
                                        canEditCorrespondence,
                                        ...(showXmlAlert && { xmlErrors }),
                                        onUploadLoadingChange: setIsUploading,
                                        typeSnippetId: watchFields.typeSnippetId,
                                        hasServiceRequestId,
                                        isPxConnectContext, // gate: GDV vs PXC im Typ-Filter
                                        ...(watchFields.typeSnippetId?.key === 'GDV' && {
                                            supportedFileTypes: GDV_SUPPORTED_FILE_TYPES,
                                        }),
                                        mapOptions: ({ label, ...snippet }: Option) => {
                                            const ranks = {
                                                Angebot: 0,
                                                Terminmitteilung: 1,
                                                'Zwischeninfo - Zur Ablage': 2,
                                                'Zwischeninfo - Reaktion erforderlich': 3,
                                                Rechnung: 4,
                                                Projektabschluss: 5,
                                                Nachtrag: 6,
                                                Stornierung: 7,
                                            };
                                            return {
                                                ...snippet,
                                                label,
                                                rank: ranks[label as keyof typeof ranks],
                                            };
                                        },
                                        sortOptions: (a: Option, b: Option) => {
                                            return a?.rank && b?.rank ? a?.rank - b?.rank : 0;
                                        },
                                        filterOptions: ({ label, value, key }: Option) => {
                                            // PXC-Kontext: „Nachtrag" und „Stornierung" vergibt nur das
                                            // System, „Terminerfassung" braucht das PX-Terminrecht. Ein an
                                            // dieser Korrespondenz schon gesetzter Subtyp bleibt immer
                                            // wählbar, sonst ließe sich ein alter Eintrag nicht mehr öffnen.
                                            if (isPxcType) {
                                                if (correspondence?.subtypSnippetId === value)
                                                    return true;
                                                if (
                                                    key &&
                                                    PXC_SUBTYP_NUR_SYSTEM_KUERZEL.includes(key)
                                                )
                                                    return false;
                                                if (key === PXC_SUBTYP_TERMINERFASSUNG_KUERZEL)
                                                    return canCreatePxcTermin;
                                                return true;
                                            }
                                            // GDV: Deny-Liste (außer bereits gewählter Subtyp).
                                            return correspondence?.subtypSnippetId === value
                                                ? true
                                                : !['Angebot', 'Stornierung', 'Nachtrag'].includes(
                                                      label
                                                  );
                                        },
                                        meta: {
                                            employeeId: {
                                                value: correspondence?.sachbearbeiter?.id,
                                                label: join([
                                                    correspondence?.sachbearbeiter?.name,
                                                    correspondence?.sachbearbeiter?.vorname,
                                                ]),
                                            },
                                        },
                                    },
                                })}
                            </React.Fragment>
                        ))}
                    </SimpleGrid>
                </Form>
                {showXmlAlert && xmlErrors.length > 0 && (
                    <GridItem colSpan={2} mb={6}>
                        <Alert status="warning">
                            <AlertIcon />
                            {xmlErrors.includes(NO_INVOICE_DOCUMENTS)
                                ? `Für eine ${
                                      isPxcType ? 'PX Connect' : 'GDV'
                                  }-Nachricht vom Subtyp "Rechnung" muss mindestens ein Dokument vom Typ "Rechnung" angehängt werden.`
                                : xmlErrors.includes(NO_OFFER_DOCUMENTS)
                                ? 'Für eine PX Connect-Nachricht vom Subtyp "Angebot" muss ein aus einem Angebot erzeugtes Dokument angehängt werden.'
                                : xmlErrors.length === 1
                                ? 'Die beigefügte Rechnung hat kein valides XML.'
                                : 'Die beigefügten Rechnungen haben keine valides XML.'}
                        </Alert>
                    </GridItem>
                )}
            </ModalBody>
        </ModalContent>
    );
};
